import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Truck, Building2 } from "lucide-react";
import { useCart } from "../lib/CartContext";
import { useAuth, loginWithGoogle } from "../lib/AuthContext";
import { api, formatPrice, imgUrl } from "../lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { items, remove, total: subtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [couriers, setCouriers] = useState([]);
  const [paymentMethod] = useState("bank_transfer");
  const [courier, setCourier] = useState("fan");
  const [addr, setAddr] = useState({ full_name: "", phone: "", address: "", city: "", county: "", postal_code: "" });

  useEffect(() => { api.get("/couriers").then(r => setCouriers(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user?.name) setAddr(a => ({ ...a, full_name: a.full_name || user.name })); }, [user]);

  const courierObj = couriers.find(c => c.id === courier) || { price: 0 };
  const grandTotal = subtotal + (courierObj.price || 0);

  const validAddr = () => addr.full_name && addr.phone && addr.address && addr.city && addr.county;

  const checkout = async () => {
    if (!user) { loginWithGoogle(); return; }
    if (!validAddr()) { toast.error("Completează adresa de livrare"); return; }
    setLoading(true);
    try {
      const r = await api.post("/checkout/session", {
        origin_url: window.location.origin,
        shipping_address: addr,
        courier,
        payment_method: paymentMethod,
      });
      if (r.data.payment_method === "bank_transfer") {
        navigate(`/checkout/bank-transfer?order_id=${r.data.order_id}`);
      } else if (r.data.url) {
        window.location.href = r.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la inițierea plății");
      setLoading(false);
    }
  };

  return (
    <main className="pt-32 pb-20 min-h-screen" data-testid="cart-page">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <header className="mb-12 text-center">
          <p className="label-luxe text-[#D4AF37] mb-3">Coșul Meu</p>
          <h1 className="h-display text-5xl sm:text-6xl text-[#F8F4E6]">Comanda Ta</h1>
        </header>

        {items.length === 0 ? (
          <div className="text-center py-24">
            <p className="font-serif text-3xl text-[#A39E8A] mb-6">Coșul este gol</p>
            <Link to="/shop" className="btn-gold">Descoperă Colecția</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_380px] gap-12">
            <div>
              <ul className="space-y-6 mb-12">
                {items.map(({ product, quantity }) => (
                  <li key={product.id} className="flex gap-6 pb-6 border-b border-[#D4AF37]/10" data-testid={`cart-row-${product.id}`}>
                    <Link to={`/products/${product.id}`} className="w-32 h-40 flex-shrink-0">
                      <img src={imgUrl(product.images?.[0])} alt={product.name} className="w-full h-full object-cover" />
                    </Link>
                    <div className="flex-1">
                      <p className="label-luxe">{product.category}</p>
                      <h3 className="font-serif text-2xl text-[#F8F4E6] mt-1 mb-3">{product.name}</h3>
                      <p className="text-sm text-[#A39E8A] mb-4">Cantitate: {quantity}</p>
                      <div className="flex items-baseline gap-6">
                        <span className="font-serif text-2xl text-[#D4AF37]">{formatPrice(product.price * quantity)}</span>
                        <button onClick={() => remove(product.id)} className="text-sm text-[#A39E8A] hover:text-red-400 flex items-center gap-2" data-testid={`remove-row-${product.id}`}>
                          <Trash2 size={14} /> Elimină
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Shipping Address */}
              {user && (
                <section className="mb-10" data-testid="shipping-section">
                  <p className="label-luxe text-[#D4AF37] mb-5">Adresă de Livrare</p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <input className="input-luxe" placeholder="Nume complet" value={addr.full_name} onChange={e => setAddr({...addr, full_name: e.target.value})} data-testid="addr-name" />
                    <input className="input-luxe" placeholder="Telefon" value={addr.phone} onChange={e => setAddr({...addr, phone: e.target.value})} data-testid="addr-phone" />
                    <input className="input-luxe sm:col-span-2" placeholder="Adresă (stradă, număr, bloc, apt)" value={addr.address} onChange={e => setAddr({...addr, address: e.target.value})} data-testid="addr-street" />
                    <input className="input-luxe" placeholder="Oraș" value={addr.city} onChange={e => setAddr({...addr, city: e.target.value})} data-testid="addr-city" />
                    <input className="input-luxe" placeholder="Județ" value={addr.county} onChange={e => setAddr({...addr, county: e.target.value})} data-testid="addr-county" />
                    <input className="input-luxe" placeholder="Cod poștal" value={addr.postal_code} onChange={e => setAddr({...addr, postal_code: e.target.value})} data-testid="addr-postal" />
                  </div>
                </section>
              )}

              {/* Courier */}
              {user && (
                <section className="mb-10" data-testid="courier-section">
                  <p className="label-luxe text-[#D4AF37] mb-5">Metoda de Livrare</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {couriers.map(c => (
                      <label key={c.id} className={`flex items-center gap-4 p-4 border cursor-pointer transition ${courier === c.id ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#D4AF37]/15 hover:border-[#D4AF37]/40"}`} data-testid={`courier-${c.id}`}>
                        <input type="radio" name="courier" checked={courier === c.id} onChange={() => setCourier(c.id)} className="accent-[#D4AF37]" />
                        <Truck size={18} className="text-[#D4AF37]" />
                        <div className="flex-1">
                          <p className="text-[#F8F4E6] font-medium">{c.name}</p>
                          <p className="text-xs text-[#A39E8A]">{c.estimated_days}</p>
                        </div>
                        <span className="font-serif text-lg text-[#D4AF37]">{c.price === 0 ? "Gratis" : `${c.price} RON`}</span>
                      </label>
                    ))}
                  </div>
                </section>
              )}

              {/* Payment Method - Bank Transfer only */}
              {user && (
                <section className="mb-10" data-testid="payment-section">
                  <p className="label-luxe text-[#D4AF37] mb-5">Metoda de Plată</p>
                  <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-5 flex items-center gap-4" data-testid="pay-transfer">
                    <Building2 size={22} className="text-[#D4AF37]" />
                    <div className="flex-1">
                      <p className="text-[#F8F4E6] font-medium">Transfer Bancar</p>
                      <p className="text-xs text-[#A39E8A]">Primești IBAN-ul + codul de plată după finalizarea comenzii. Comanda va fi procesată după confirmarea plății.</p>
                    </div>
                  </div>
                </section>
              )}
            </div>

            <aside className="bg-[#0a0a0a] border border-[#D4AF37]/15 p-8 h-fit lg:sticky lg:top-28" data-testid="order-summary">
              <p className="label-luxe text-[#D4AF37] mb-6">Sumar Comandă</p>
              <div className="space-y-3 text-sm pb-6 border-b border-[#D4AF37]/10">
                <div className="flex justify-between text-[#A39E8A]"><span>Subtotal</span><span className="text-[#F8F4E6]">{formatPrice(subtotal)}</span></div>
                <div className="flex justify-between text-[#A39E8A]"><span>Livrare</span><span className="text-[#F8F4E6]">{courierObj.price === 0 ? "Gratis" : formatPrice(courierObj.price || 0)}</span></div>
              </div>
              <div className="flex justify-between items-baseline mt-6 mb-8">
                <span className="label-luxe">Total</span>
                <span className="font-serif text-3xl text-[#D4AF37]">{formatPrice(grandTotal)}</span>
              </div>
              <button onClick={checkout} disabled={loading || !user} className="btn-gold w-full justify-center" data-testid="cart-checkout">
                {loading ? "Procesare..." : !user ? "Conectează-te" : "Plasează Comanda"}
              </button>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#A39E8A] mt-4 text-center">
                Plată prin transfer bancar
              </p>
            </aside>
          </div>
        )}
      </div>
    </main>
  );
}
